import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Select2Dropdown from '../common/Select2Dropdown';
import ConfirmDialog from '../common/ConfirmDialog';
import { usePermissions } from '../../services/PermissionsContext';
import useSortableTable from '../common/useSortableTable';

const API_URL = '/api/admin/company-bank-accounts/';
const BANKS_API_URL = '/api/masters/banks/';

const authHeaders = (json = true) => {
    const token = localStorage.getItem('kk_token');
    return {
        ...(json ? { 'Content-Type': 'application/json' } : {}),
        'Authorization': `Bearer ${token}`
    };
};

const CURRENCY_OPTIONS = [
    { id: 'TZS', text: 'TZS' },
    { id: 'USD', text: 'USD' },
    { id: 'EUR', text: 'EUR' },
    { id: 'GBP', text: 'GBP' }
];

const FEE_TAB_OPTIONS = [
    { id: 'TISS', text: 'TISS' },
    { id: 'TIPS', text: 'TIPS' },
    { id: 'GEPG', text: 'GEPG' }
];

const createBracketRow = () => ({
    min_amount: '',
    max_amount: '',
    rate: ''
});

const normalizeBracketRows = (rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map(row => ({
        min_amount: row?.min_amount ?? row?.min ?? '',
        max_amount: row?.max_amount ?? row?.max ?? '',
        rate: row?.rate ?? row?.amount ?? ''
    }));
};

const getBracketFieldKey = (tabId) => tabId === 'TIPS' ? 'tips_brackets' : tabId === 'GEPG' ? 'gepg_brackets' : 'tiss_brackets';

const BankAccounts = () => {
    const { hasPermission } = usePermissions();
    const MODULE_NAME = 'BANK ACCOUNTS';
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFeeTab, setActiveFeeTab] = useState('TISS');

    // Form State
    const [formData, setFormData] = useState({
        bank: '',
        short_name: '',
        branch: '',
        swift_code: '',
        currency: 'TZS',
        account_number: '',
        tiss_brackets: [],
        tips_brackets: []
    });

    // Master Data
    const [banks, setBanks] = useState([]);
    const [bankOptions, setBankOptions] = useState([]);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(API_URL, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to fetch bank accounts');
            const data = await res.json();
            setAccounts(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBanks = useCallback(async () => {
        try {
            const res = await fetch(BANKS_API_URL, { headers: authHeaders(false) });
            if (!res.ok) throw new Error('Failed to fetch banks list');
            const data = await res.json();
            setBanks(data);
            setBankOptions(data.map(b => ({ id: b.id, text: b.name, swift_code: b.swift_code })));
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        if (hasPermission(MODULE_NAME, 'can_view')) {
            fetchAccounts();
            fetchBanks();
        }
    }, [hasPermission, fetchAccounts, fetchBanks]);

    const handleEdit = (acc) => {
        setEditingAccount(acc);
        setFormData({
            bank: acc.bank,
            short_name: acc.short_name || '',
            branch: acc.branch,
            swift_code: acc.swift_code,
            currency: acc.currency,
            account_number: acc.account_number,
            tiss_brackets: normalizeBracketRows(acc.tiss_brackets),
            tips_brackets: normalizeBracketRows(acc.tips_brackets),
            gepg_brackets: normalizeBracketRows(acc.gepg_brackets)
        });
        setActiveFeeTab('TISS');
        setModalOpen(true);
    };

    const handleAdd = () => {
        setEditingAccount(null);
        setFormData({
            bank: '',
            short_name: '',
            branch: '',
            swift_code: '',
            currency: 'TZS',
            account_number: '',
            tiss_brackets: [],
            tips_brackets: [],
            gepg_brackets: []
        });
        setActiveFeeTab('TISS');
        setModalOpen(true);
    };

    const handleBankChange = (bankId) => {
        const selectedBank = banks.find(b => b.id === bankId);
        setFormData(prev => ({
            ...prev,
            bank: bankId,
            swift_code: selectedBank ? (selectedBank.swift_code || '') : ''
        }));
    };

    const updateBracketRow = (tabKey, index, field, value) => {
        setFormData(prev => {
            const nextRows = [...(prev[tabKey] || [])];
            nextRows[index] = {
                ...(nextRows[index] || createBracketRow()),
                [field]: value
            };

            return {
                ...prev,
                [tabKey]: nextRows
            };
        });
    };

    const addBracketRow = (tabKey) => {
        setFormData(prev => ({
            ...prev,
            [tabKey]: [...(prev[tabKey] || []), createBracketRow()]
        }));
    };

    const removeBracketRow = (tabKey, index) => {
        setFormData(prev => ({
            ...prev,
            [tabKey]: (prev[tabKey] || []).filter((_, rowIndex) => rowIndex !== index)
        }));
    };

    const renderBracketSection = (tabId) => {
        const tabKey = getBracketFieldKey(tabId);
        const rows = formData[tabKey] || [];

        return (
            <div className="form-section" style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <h4 style={{ margin: 0 }}>{tabId} Brackets</h4>
                    <button type="button" className="dialog-btn dialog-btn-primary" onClick={() => addBracketRow(tabKey)}>
                        Add Row
                    </button>
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {FEE_TAB_OPTIONS.map(option => (
                        <button
                            key={option.id}
                            type="button"
                            className="dialog-btn"
                            onClick={() => setActiveFeeTab(option.id)}
                            style={{
                                background: activeFeeTab === option.id ? '#1f6feb' : '#e9eef6',
                                color: activeFeeTab === option.id ? '#fff' : '#1f2937',
                                border: 'none'
                            }}
                        >
                            {option.text}
                        </button>
                    ))}
                </div>
                {rows.length === 0 ? (
                    <div style={{ padding: '16px', border: '1px dashed #cbd5e1', borderRadius: 8, color: '#64748b' }}>
                        No rows added yet. Click Add Row to create a bracket.
                    </div>
                ) : (
                    <div style={{ maxHeight: '190px', overflowY: 'auto', paddingRight: '6px', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px' }}>
                    <div style={{ display: 'grid', gap: 12 }}>
                        {rows.map((row, index) => (
                            <div key={`${tabKey}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label>Minimum Amount</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.min_amount}
                                        onChange={e => updateBracketRow(tabKey, index, 'min_amount', e.target.value)}
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label>Maximum Amount</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.max_amount}
                                        onChange={e => updateBracketRow(tabKey, index, 'max_amount', e.target.value)}
                                        placeholder="Leave blank for no upper limit"
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label>Rate</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={row.rate}
                                        onChange={e => updateBracketRow(tabKey, index, 'rate', e.target.value)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="dialog-btn dialog-btn-secondary"
                                    onClick={() => removeBracketRow(tabKey, index)}
                                    style={{ height: 40 }}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                    </div>
                )}
            </div>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasPermission(MODULE_NAME, editingAccount ? 'can_edit' : 'can_create')) {
            toast.error("Permission denied");
            return;
        }

        try {
            const payload = {
                ...formData,
                tiss_brackets: (formData.tiss_brackets || []).filter(row => row.min_amount !== '' || row.max_amount !== '' || row.rate !== ''),
                tips_brackets: (formData.tips_brackets || []).filter(row => row.min_amount !== '' || row.max_amount !== '' || row.rate !== ''),
                gepg_brackets: (formData.gepg_brackets || []).filter(row => row.min_amount !== '' || row.max_amount !== '' || row.rate !== '')
            };

            const url = editingAccount ? `${API_URL}${editingAccount.id}/` : API_URL;
            const method = editingAccount ? 'PUT' : 'POST';
            
            const res = await fetch(url, {
                method,
                headers: authHeaders(),
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(JSON.stringify(err));
            }

            toast.success(editingAccount ? 'Account updated' : 'Account created');
            setModalOpen(false);
            fetchAccounts();
        } catch (err) {
            toast.error("Error saving account: " + err.message);
        }
    };

    const filteredAccounts = accounts.filter(acc =>
        (acc.bank_name && acc.bank_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (acc.short_name && acc.short_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (acc.account_number && acc.account_number.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Sort array
    const { items: sortedAccounts, requestSort, getSortIndicator } = useSortableTable(filteredAccounts, { key: 'short_name', direction: 'ascending' });

    if (!hasPermission(MODULE_NAME, 'can_view')) return <div>Access Denied</div>;

    return (
        <div className="page-container">
            <div className="admin-header">
                <h2>Company Bank Accounts</h2>
            </div>

            <div className="filters-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 16 }}>
                <div className="filter-item">
                    <input 
                        type="text" 
                        value={searchTerm} 
                        onChange={e => setSearchTerm(e.target.value)} 
                        placeholder="Search accounts..." 
                        className="search-input"
                    />
                </div>
                {hasPermission(MODULE_NAME, 'can_create') && (
                    <button className="add-btn" onClick={handleAdd}>
                        <i className="fa fa-plus" style={{ marginRight: 8 }} />
                        Add New Account
                    </button>
                )}
            </div>

            <div className="table-responsive">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('short_name')} style={{cursor: 'pointer'}}>Short Name {getSortIndicator('short_name')}</th>
                            <th onClick={() => requestSort('bank_name')} style={{cursor: 'pointer'}}>Bank Name {getSortIndicator('bank_name')}</th>
                            <th onClick={() => requestSort('branch')} style={{cursor: 'pointer'}}>Branch {getSortIndicator('branch')}</th>
                            <th onClick={() => requestSort('swift_code')} style={{cursor: 'pointer'}}>Swift Code {getSortIndicator('swift_code')}</th>
                            <th onClick={() => requestSort('currency')} style={{cursor: 'pointer'}}>Currency {getSortIndicator('currency')}</th>
                            <th onClick={() => requestSort('account_number')} style={{cursor: 'pointer'}}>Account Number {getSortIndicator('account_number')}</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="7">Loading...</td></tr>}
                        {!loading && sortedAccounts.length === 0 && <tr><td colSpan="7" className="no-data">No accounts found.</td></tr>}
                        {sortedAccounts.map(acc => (
                            <tr key={acc.id}>
                                <td>{acc.short_name || '-'}</td>
                                <td>{acc.bank_name}</td>
                                <td>{acc.branch}</td>
                                <td>{acc.swift_code}</td>
                                <td>{acc.currency}</td>
                                <td>{acc.account_number}</td>
                                <td className="actions">
                                    {hasPermission(MODULE_NAME, 'can_edit') && (
                                        <button className="icon-btn" title="Edit" onClick={() => handleEdit(acc)}>
                                            <i className="fa fa-pen"></i>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modalOpen && (
                <div className="modal-overlay">
                    <div className="dialog-container" style={{ maxWidth: '700px', width: '90%' }}>
                        <div className="dialog-header">
                            <h3>{editingAccount ? 'Edit Account' : 'Add New Account'}</h3>
                            <button onClick={() => setModalOpen(false)} className="icon-btn" style={{ color: 'white' }} title="Close">&times;</button>
                        </div>
                        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
                            <div className="dialog-body">
                                <div className="form-section">
                                    <h4>Account Details</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                        <div className="form-group">
                                            <label>Bank <span className="required"></span></label>
                                            <Select2Dropdown
                                                options={bankOptions}
                                                value={formData.bank}
                                                onChange={handleBankChange}
                                                valueKey="id"
                                                displayKey="text"
                                                placeholder="Select Bank"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Short Name (Optional)</label>
                                            <input 
                                                type="text"
                                                value={formData.short_name || ''}
                                                onChange={e => setFormData({...formData, short_name: e.target.value.toUpperCase()})}
                                                placeholder="e.g. CRDB-TZS, NMB-USD"
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Branch <span className="required"></span></label>
                                            <input 
                                                type="text"
                                                value={formData.branch}
                                                onChange={e => setFormData({...formData, branch: e.target.value.toUpperCase()})}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Swift Code <span className="required"></span></label>
                                            <input 
                                                type="text"
                                                value={formData.swift_code}
                                                onChange={e => setFormData({...formData, swift_code: e.target.value.toUpperCase()})}
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Currency <span className="required"></span></label>
                                            <Select2Dropdown
                                                options={CURRENCY_OPTIONS}
                                                value={formData.currency}
                                                onChange={val => setFormData({...formData, currency: val})}
                                                valueKey="id"
                                                displayKey="text"
                                                required
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>Account Number <span className="required"></span></label>
                                            <input 
                                                type="text"
                                                value={formData.account_number}
                                                onChange={e => setFormData({...formData, account_number: e.target.value.toUpperCase()})}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="form-section" style={{ marginTop: 20 }}>
                                    {renderBracketSection(activeFeeTab)}
                                </div>
                            </div>
                            <div className="dialog-footer">
                                <button type="button" className="dialog-btn dialog-btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
                                <button type="submit" className="dialog-btn dialog-btn-primary">
                                    {editingAccount ? 'Update Account' : 'Create Account'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BankAccounts;
